import { ALLOWED_ATTACHMENT_MIME_TYPES, type NoteColor, type NoteType } from '@keep/shared';
import {
  Archive,
  ArchiveRestore,
  BellPlus,
  EllipsisVertical,
  ImagePlus,
  Palette,
  RotateCcw,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { IconButton } from '../../components/ui/icon-button';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../components/ui/menu';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { cn } from '../../lib/cn';
import { ColorPicker } from './color-picker';
import { LabelPicker } from './label-picker';
import { ReminderPicker } from './reminder-picker';

export interface NoteToolbarProps {
  color: NoteColor;
  reminderAt: string | null;
  labelIds: readonly string[];
  archived: boolean;
  type: NoteType;
  trashed: boolean;
  size?: 'sm' | 'md';
  className?: string;
  /** Prévient le parent qu'un menu est ouvert (la barre d'une carte doit alors rester visible). */
  onMenuOpenChange?: (open: boolean) => void;
  onColor?: (color: NoteColor) => void;
  onReminder?: (reminderAt: string | null) => void;
  onToggleLabel?: (labelId: string, enabled: boolean) => void;
  onArchive?: () => void;
  onTrash?: () => void;
  onRestore?: () => void;
  onDeleteForever?: () => void;
  onCopy?: () => void;
  onToggleChecklist?: () => void;
  onAddImages?: (files: File[]) => void;
  onCollaborators?: () => void;
}

/**
 * Barre d'actions d'une note (carte, éditeur ou compositeur).
 * Une action n'est affichée que si son gestionnaire est fourni.
 */
export function NoteToolbar(props: NoteToolbarProps) {
  const { size = 'sm', trashed } = props;
  const fileInput = useRef<HTMLInputElement>(null);
  const [openMenu, setOpenMenu] = useState<null | 'reminder' | 'color' | 'more' | 'labels'>(null);

  const setOpen = (menu: typeof openMenu) => {
    setOpenMenu(menu);
    props.onMenuOpenChange?.(menu !== null);
  };
  const popoverProps = (menu: NonNullable<typeof openMenu>) => ({
    open: openMenu === menu,
    onOpenChange: (open: boolean) => setOpen(open ? menu : null),
  });

  if (trashed) {
    return (
      <div
        className={cn('flex items-center', props.className)}
        onClick={(e) => e.stopPropagation()}
      >
        {props.onDeleteForever && (
          <IconButton
            icon={Trash2}
            size={size}
            label="Supprimer définitivement"
            onClick={props.onDeleteForever}
          />
        )}
        {props.onRestore && (
          <IconButton icon={RotateCcw} size={size} label="Restaurer" onClick={props.onRestore} />
        )}
      </div>
    );
  }

  const hasMore = props.onTrash || props.onToggleLabel || props.onCopy || props.onToggleChecklist;

  return (
    <div
      className={cn('flex items-center gap-0.5 sm:gap-1', props.className)}
      onClick={(e) => e.stopPropagation()}
    >
      {props.onReminder && (
        <Popover {...popoverProps('reminder')}>
          <PopoverTrigger asChild>
            <IconButton icon={BellPlus} size={size} label="Me le rappeler" />
          </PopoverTrigger>
          <PopoverContent>
            <ReminderPicker
              value={props.reminderAt}
              onChange={props.onReminder}
              onDone={() => setOpen(null)}
            />
          </PopoverContent>
        </Popover>
      )}
      {props.onCollaborators && (
        <IconButton
          icon={UserPlus}
          size={size}
          label="Collaborateur"
          onClick={props.onCollaborators}
        />
      )}
      {props.onColor && (
        <Popover {...popoverProps('color')}>
          <PopoverTrigger asChild>
            <IconButton icon={Palette} size={size} label="Options d'arrière-plan" />
          </PopoverTrigger>
          <PopoverContent onOpenAutoFocus={(event) => event.preventDefault()}>
            <ColorPicker value={props.color} onChange={props.onColor} />
          </PopoverContent>
        </Popover>
      )}
      {props.onAddImages && (
        <>
          <IconButton
            icon={ImagePlus}
            size={size}
            label="Ajouter une image"
            onClick={() => fileInput.current?.click()}
          />
          <input
            ref={fileInput}
            type="file"
            accept={ALLOWED_ATTACHMENT_MIME_TYPES.join(',')}
            multiple
            hidden
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = '';
              if (files.length) props.onAddImages?.(files);
            }}
          />
        </>
      )}
      {props.onArchive && (
        <IconButton
          icon={props.archived ? ArchiveRestore : Archive}
          size={size}
          label={props.archived ? 'Désarchiver' : 'Archiver'}
          onClick={props.onArchive}
        />
      )}
      {hasMore && (
        <Popover {...popoverProps('labels')}>
          {/* Le sélecteur de libellés s'ancre sur le bouton « Plus », comme dans Keep. */}
          <PopoverAnchor asChild>
            <span className="inline-flex">
              <Menu {...popoverProps('more')} modal={false}>
                <MenuTrigger asChild>
                  <IconButton icon={EllipsisVertical} size={size} label="Plus" />
                </MenuTrigger>
                <MenuContent>
                  {props.onTrash && <MenuItem onSelect={props.onTrash}>Supprimer la note</MenuItem>}
                  {props.onToggleLabel && (
                    // Laisse le menu se fermer avant d'ouvrir le sélecteur (sinon il récupère le focus).
                    <MenuItem onSelect={() => setTimeout(() => setOpen('labels'))}>
                      {props.labelIds.length ? 'Modifier les libellés' : 'Ajouter un libellé'}
                    </MenuItem>
                  )}
                  {props.onCopy && <MenuItem onSelect={props.onCopy}>Créer une copie</MenuItem>}
                  {props.onToggleChecklist && (
                    <MenuItem onSelect={props.onToggleChecklist}>
                      {props.type === 'checklist'
                        ? 'Masquer les cases à cocher'
                        : 'Afficher les cases à cocher'}
                    </MenuItem>
                  )}
                </MenuContent>
              </Menu>
            </span>
          </PopoverAnchor>
          {props.onToggleLabel && (
            <PopoverContent>
              <LabelPicker selected={props.labelIds} onToggle={props.onToggleLabel} />
            </PopoverContent>
          )}
        </Popover>
      )}
    </div>
  );
}
